import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { MarginOrderDirection } from '@swyftx/margin/build/types';

import { Big } from '@shared/safe-big';
import { cn } from '@shared/utils/lib/ui';

import { observer } from 'mobx-react-lite';
import { useMarginInstruments } from 'src/lib/margin/hooks/instruments';
import { useMarginPrices } from 'src/lib/margin/hooks/prices';

import { MarginTradePanelBuySellTab } from './MarginTradePanelBuySellTab';

type Props = {
  tradeSide: MarginOrderDirection;
  setSide: (tradeSide: MarginOrderDirection) => void;
};

const MarginTradePanelBuySellTabs: React.FC<Props> = observer(({ tradeSide, setSide }) => {
  const { selectedMarket } = useMarginInstruments();
  const { selectedMarketPrice } = useMarginPrices();

  const { t } = useTranslation('margin');

  const tabClass = useMemo(() => {
    if ((!selectedMarket?.short && !selectedMarket?.long) || !selectedMarket.open) {
      return 'bg-color-background-surface-disabled';
    }

    if (tradeSide === MarginOrderDirection.Long) return 'bg-color-background-success';

    return 'bg-color-background-error';
  }, [selectedMarket, tradeSide]);

  return (
    <div className='w-full px-12 pb-8 pt-12'>
      <FlexLayout
        className='relative h-[60px] w-full cursor-pointer rounded-12 bg-color-background-surface-secondary p-4'
        justifyContent='space-between'
      >
        <MarginTradePanelBuySellTab
          title={t(`tradeSide.${MarginOrderDirection.Long}`)}
          loading={!selectedMarketPrice}
          alignItems='center'
          tabValue={MarginOrderDirection.Long}
          disabled={!selectedMarket?.long || !selectedMarket.open}
          value={Big(selectedMarketPrice?.ask)}
          priceScale={selectedMarket?.precision}
          tradeSide={tradeSide}
          setSide={setSide}
        />
        <MarginTradePanelBuySellTab
          title={t(`tradeSide.${MarginOrderDirection.Short}`)}
          loading={!selectedMarketPrice}
          alignItems='center'
          disabled={!selectedMarket?.short || !selectedMarket.open}
          value={Big(selectedMarketPrice?.bid)}
          priceScale={selectedMarket?.precision}
          tabValue={MarginOrderDirection.Short}
          tradeSide={tradeSide}
          setSide={setSide}
        />
        <FlexLayout
          className={cn(
            'absolute h-[calc(100%-8px)] w-[calc(50%-4px)] rounded-8 transition-transform',
            tabClass,
            tradeSide === MarginOrderDirection.Long ? 'translate-x-[0%]' : 'translate-x-[100%]',
          )}
        />
      </FlexLayout>
    </div>
  );
});

export { MarginTradePanelBuySellTabs };
